/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import axios from 'axios';
import { HC_PROPS } from 'lion-vue';
import { feathers } from '@/feathers-client';
import {
  LIONDESK_STAFF_CHARGEBEE_ID,
  BOLDLEADS_STAFF_CHARGEBEE_ID,
  LEADSPLUS_STAFF_CHARGEBEE_ID,
} from '@/Core/config/addons';
import { GMAIL_DELIVERABILITY_FF } from '@/Email/config/layout';
import { STATUS_SUBSCRIBED, STATUS_TRIAL, MLS_AOR_SKUS } from '../config/subscription';


export default {
  namespaced: true,
  state: {
    user: null,
    userLoading: null,
    retryUpdateEmail: 0,
    emailError: null,
    emailUpdateSuccess: false,
    emailToChange: '',
    emailUpdateLoading: false,
    userError: false,
    serverDate: null,
    subscription: null,
    userTeams: [],
    userTeamsLoading: false,
    userTeamsError: null,
    cellPhoneValidation: {
      error: null,
      loading: false,
      retryTime: 0,
      data: null,
    },
  },
  mutations: {
    setCellPhoneValidationLoading(state, value) {
      state.cellPhoneValidation.loading = value;
    },
    setCellPhoneValidationRetry(state, time) {
      state.cellPhoneValidation.retryTime = time;
    },
    setCellPhoneValidationData(state, data) {
      state.cellPhoneValidation.data = data;
      state.cellPhoneValidation.error = null;
      state.cellPhoneValidation.loading = false;
    },
    setCellPhoneValidationError(state, error) {
      state.cellPhoneValidation.error = error;
      state.cellPhoneValidation.loading = false;
    },
    clearUpdateEmailMessages(state, value) {
      state.retryUpdateEmail = 0;
      state.emailUpdateSuccess = false;
      state.emailError = null;
      state.emailUpdateLoading = false;
    },
    setRetryUpdateEmail(state, time) {
      state.retryUpdateEmail = time;
    },
    setEmailUpdateLoading(state, value) {
      state.emailUpdateLoading = value;
    },
    setEmailToChange(state, email) {
      state.emailToChange = email;
    },
    setEmailUpdateSuccess(state, email) {
      state.emailUpdateSuccess = true;
      state.emailToChange = email;
      state.emailUpdateLoading = false;
      state.emailError = null;
    }, 
    setEmailError(state, error) {
      state.emailError = error;
      state.emailUpdateSuccess = false;
      state.emailUpdateLoading = false;
    },
    userLoading(state, payload) {
      state.userError = null;
      state.userLoading = payload;
    },
    setUser(state, payload) {
      state.user = payload;
      state.userLoading = false;
    },
    userError(state, payload) {
      state.userError = payload;
      state.userLoading = false;
    },
    setServerDate(state, payload) {
      state.serverDate = payload;
    },
    setSubscription(state, payload) {
      state.subscription = payload;
    },
    setUserTeams(state, payload) {
      state.userTeams = payload;
    },
    setUserTeamsLoading(state, bool) {
      state.userTeamsLoading = bool;
    },
    setUserTeamsError(state, payload) {
      state.userTeamsError = payload;
    },
  },
  getters: {
    serverYear: state => {
      return new Date(state.serverDate).getFullYear();
    },
    userHasDeleteStaticList(state) {
      if (!state.user) return false;
      const { feature_flags: featureFlags } = state.user;
      return featureFlags.includes('delete_static_list');
    },
    hasMlsUserAccessToTextFeature(state) {
      const CCAR_PLAN = 'ccar';
      const BEAR_PLAN = 'bear';
      if (!state.user) return false;
      const { txt_plan: txtPlan } = state.user.subscriptionOverview;
      return txtPlan === CCAR_PLAN || txtPlan === BEAR_PLAN;
    },
    userHasNylasSend(state) {
      if (!state.user) return false;
      const { feature_flags: featureFlags } = state.user;
      return featureFlags.includes('nylas-send');
    },
    showFromEmailField(state) {
      if (!state.user) return false;
      const { feature_flags: featureFlags } = state.user;
      return featureFlags.includes('nylas-send') || featureFlags.includes(GMAIL_DELIVERABILITY_FF);
    },
    userHasExchangeFeature(state) {
      if (!state.user) return false;
      const { feature_flags: featureFlags } = state.user;
      return featureFlags.includes('exchange');
    },
    userHasFeatureFlag(state) {
      return function(featureFlag) {
        if (!state.user) return false;
        const { feature_flags: featureFlags } = state.user;
        return featureFlags.includes(featureFlag);
      };
    },
    isSubscribed(state) {
      return state.user.subscriptionOverview.txt_status === STATUS_SUBSCRIBED;
    },
    hasTrial(state) {
      return state.user && state.user.subscriptionOverview.txt_status === STATUS_TRIAL;
    },
    isOnBrokerPurchasedPlan(state) {
      const { user } = state;
      const { subscriptionOverview } = user;
      return subscriptionOverview.details.id_user_purchased !== user.id;
    },
    hasUserMLS(state) {
      const v2Plans = ['starter', 'pro+', 'elite'];
      return (
        state.user &&
        state.user.subscriptionOverview.mls_subscription &&
        !v2Plans.includes(state.user.subscriptionOverview.txt_plan)
      );
    },
    shouldGoToLeadsPlusFlow(state) {
      if (!state.user) return false;
      const { feature_flags: featureFlags } = state.user;
      
      const targetDate = new Date(2023, 7, 31); // Aug 31, 2023
      targetDate.setUTCHours(0, 0, 0, 0);
      const dateNow = new Date();
      dateNow.setUTCHours(0, 0, 0, 0);
      return dateNow > targetDate;
    },
    hasAccessToFeature(state) {
      return function(feature) {
        if (!state.user) return false;
        const { features } = state.user;
        return features[feature] && features[feature].has_access;
      }
    },
    getUserFullName(state) {
      if (!state.user) return '';
      const { first_name: firstName, last_name: lastName } = state.user;
      return `${firstName} ${lastName}`;
    },
    isAllowedToUseNylas(state) {
      const hasSubscriptionDetails = state.user && state.user.subscriptionOverview && state.user.subscriptionOverview.details;
      if (!hasSubscriptionDetails) return false;

      const userSubscription = hasSubscriptionDetails.id_item;
      if (!userSubscription) return true;

      return !MLS_AOR_SKUS.some(sku => userSubscription.includes(sku));
    },
  },
  actions: {
    async resetUser({ commit }) {
      commit('setUser', null);
    },
    async checkForPasswordReset({ commit }, user) {
      if (!user.feature_flags.includes('reset-password')) {
        return;
      }
      const cutOffDate = Date.parse(HC_PROPS.RESET_PASSWORD_CUTOFF_DATE);
      const firstReleaseDate = Date.parse(HC_PROPS.RESET_PASSWORD_FIRST_RELEASE_DATE);
      const passChangedDate = Date.parse(user.password_changed_at);
      const createdAtDate = Date.parse(user.created_at);
      if (
        createdAtDate < firstReleaseDate &&
        (!passChangedDate || passChangedDate < firstReleaseDate) &&
        Date.now() > cutOffDate
      ) {
        const redirect = encodeURIComponent(window.location.href);
        window.location = `${process.env.VUE_APP_ACCOUNTS_APP_URL}/change-password?redirect_url=${redirect}`;
      }
    },
    async getUser({ commit, dispatch }, params = null) {
      // Skip /me call for sunset-admin routes (standalone page)
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/sunset-admin')) {
        return Promise.resolve(null);
      }
      
      const configRequest = {
        withCredentials: true
      };
      if (params) {
        configRequest.params = params;
      }
      commit('userLoading', true);
      return axios
        .get(`${process.env.VUE_APP_ACCOUNTS_API}/users/me`, configRequest)
        .then(response => {
          dispatch('checkForPasswordReset', response.data.me);
          commit('setServerDate', response.headers.date);
          commit('setUser', response.data.me);
          return response.data.me;
        })
        .catch(error => {
          commit('userError', error);
        });
    },
    async getSubscription({ commit }, idSubscription) {
      if (
        [
          LIONDESK_STAFF_CHARGEBEE_ID,
          LEADSPLUS_STAFF_CHARGEBEE_ID,
          BOLDLEADS_STAFF_CHARGEBEE_ID,
        ].includes(idSubscription)
      ) {
        return;
      }
      try {
        commit('userLoading', true);
        const response = await axios.get(
          `${process.env.VUE_APP_BILLING_API_BASE_URL}/subscriptions/${idSubscription}`,
        );
        commit('setSubscription', response.data);
      } catch (error) {
        commit('userError', error);
      }
    },
    async updateUserProfile({ commit }, data) {
      try {
        commit('userLoading', true);
        await axios.put(`${process.env.VUE_APP_ACCOUNTS_API}/users/profile`, data);
        commit('userLoading', false);
      } catch (error) {
        if (error && error.data) {
          commit('userError', error.data);
          return
        }
        commit('userError', error);
      }
    },
    async getUserTeams({ commit }, query = {}) {
      commit('setUserTeamsLoading', true);
      try {
        const response = await feathers.service('teams').find({
          query: {
            $limit: 50,
            ...query,
          },
        });
        commit('setUserTeams', response.data);
      } catch (error) {
        commit('setUserTeamsError', error);
      } finally {
        commit('setUserTeamsLoading', false);
      }
    },
    async updateDailyEmail({ commit, state }, isSubscribe) {
      const { user } = state;
      try {
        const payload = {
          subscribe: isSubscribe,
        };
        await axios.post(
          `${process.env.VUE_APP_ACCOUNTS_API}/users/${user.id}/daily-email`,
          payload,
        );
      } catch (error) {
        throw error;
      }
    },
    async sendVerificationEmailChange({ commit }, payload) {
      commit('setEmailUpdateLoading', true);
      commit('setEmailToChange', payload.email);
      try {
        const response = await axios.post(`${process.env.VUE_APP_ACCOUNTS_API}/users/profile/email`, payload);
        const currentTime = Date.now() / 1000;
        commit('setRetryUpdateEmail', response.data.expires - currentTime);
        commit('setEmailUpdateSuccess', response.data.email);
      } catch (error) {
        if (error.status && error.headers && error.status === 429) {
          commit('setRetryUpdateEmail', error.headers['retry-after']);
        }
        commit('setEmailError', error.data);
      } 
    },
    async getCellPhoneValidationCode({ commit }) {
      commit('setCellPhoneValidationLoading', true);
      try {
        const response = await axios.get(`${process.env.VUE_APP_ACCOUNTS_API}/users/profile/phone-number/validate`);
        commit('setCellPhoneValidationData', response.data.code);
      } catch (error) {
        if (error.status && error.headers && error.status === 429) {
          commit('setCellPhoneValidationRetry', error.headers['retry-after']);
        }
        commit('setCellPhoneValidationError', error.data);
      }
    },
  },
};
